/* global Janus */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectWebSocket, disconnectWebSocket, subscribeToChat, subscribeToSDPStatus, subscribeToSessionStatus, sendCommand, addOnConnectCallback, removeOnConnectCallback } from "../websocketService";
import adapter from 'webrtc-adapter';
import Janus from "janus-gateway";
import { setJanusInstance, getJanusInstance, setAudioPluginHandle, getAudioPluginHandle, getLocalMediaStream, getAllRemoteMediaStreams } from '../WebRTCManager'; // Import WebRTCManager functions
import { v4 as uuidv4 } from 'uuid'; // Import UUID for unique message IDs

window.adapter = adapter;

const RoomPage = () => {
    //const { customSessionId } = useParams();
    const [log, setLog] = useState([]);

    const navigate = useNavigate();
    const [isRecording, setIsRecording] = useState(false);
    const [directorVoiceEnabled, setDirectorVoiceEnabled] = useState(false);
    const [chatInput, setChatInput] = useState('');

    const location = useLocation();
    // Retrieve the passed state
    const { customSessionId, userName } = location.state || {};    

    console.log("RoomPage: Render (top of component) - customSessionId:", customSessionId, "userName:", userName, "location.state:", location.state);
    
    // Refs for audio and canvas elements
    const localAudioRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const localCanvasRef = useRef(null);
    const remoteCanvasRef = useRef(null);

    const chatSubscriptionRef = useRef(null);
    const sdpSubscriptionRef = useRef(null);

    //console.log("adapter shim loaded", window.adapter);
    
     // Refs for Web Audio API components
     const audioContextRef = useRef(null);
     const localAnalyserRef = useRef(null);
     const remoteAnalyserRef = useRef(null);
     const localAnimationFrameId = useRef(null);
     const remoteAnimationFrameId = useRef(null);

     //Ref to keep track of the message IDs that have been sent
    const messageIdsRef = useRef(new Set());

     // ADD THIS NEW, VERY SIMPLE useEffect - This *should* always run on mount.
    useEffect(() => {
        console.log("RoomPage: ***VERY SIMPLE useEffect RUNNING - Component Mounted***");
    }, []); // Empty dependency array ensures it runs once on mount

     // Function to initialize AudioContext
     const getOrCreateAudioContext = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return audioContextRef.current;
    };

    // Function to draw the audio visualizer
    const drawAudioVisualizer = useCallback((stream, canvasRef, analyserNodeRef, animationFrameIdRef, audioLabel) => {
        if (!canvasRef.current || !stream || stream.getAudioTracks().length === 0 || !stream.active) {
            console.log(`[${audioLabel}] Visualizer: Cannot draw. Canvas or stream missing/inactive/no audio tracks.`, { stream, canvasRef: canvasRef.current });
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
            return;
        }

        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext('2d');

        const audioContext = getOrCreateAudioContext();
        let analyser = analyserNodeRef.current;

        if (!analyser) {
            analyser = audioContext.createAnalyser();
            analyser.fftSize = 2048; // Fast Fourier Transform size
            analyser.smoothingTimeConstant = 0.7; // Smooths out the visualization
            analyserNodeRef.current = analyser;

            // Connect the stream to the analyser
            // Ensure the stream is active, otherwise createMediaStreamSource might throw
            try {
                const source = audioContext.createMediaStreamSource(stream);
                source.connect(analyser);
                console.log(`[${audioLabel}] Visualizer: MediaStreamSource and AnalyserNode connected.`);
                analyser.connect(audioContext.destination); // Connect to destination to hear audio, though already playing via <audio>
            } catch (e) {
                console.error(`[${audioLabel}] Visualizer: Error creating MediaStreamSource or connecting:`, e);
                console.error("Error creating MediaStreamSource or connecting:", e);
                if (animationFrameIdRef.current) {
                    cancelAnimationFrame(animationFrameIdRef.current);
                    animationFrameIdRef.current = null;
                }
                return;
            }
        }

        const bufferLength = analyser.frequencyBinCount; // Number of data points
        const dataArray = new Uint8Array(bufferLength); // Array to hold frequency data

        canvasCtx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous frame

        const draw = () => {
            animationFrameIdRef.current = requestAnimationFrame(draw);

            analyser.getByteFrequencyData(dataArray); // Get frequency data

            canvasCtx.fillStyle = 'rgb(0, 0, 0)'; // Background black
            canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

            const barWidth = (canvas.width / bufferLength) * 2.5;
            let barHeight;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                barHeight = dataArray[i]; // Value from 0-255

                // Choose a color based on height or a fixed color
                canvasCtx.fillStyle = `rgb(${barHeight + 100}, 50, 50)`; // Reddish bars
                canvasCtx.fillRect(x, canvas.height - barHeight / 2, barWidth, barHeight / 2); // Draw bars from bottom up

                x += barWidth + 1; // Spacing between bars
            }
        };

        // Start drawing if not already running
        if (!animationFrameIdRef.current) {
            console.log(`[${audioLabel}] Visualizer: Starting animation frame loop.`);
            draw();
        }
    }, []); // Empty dependency array means this function is created once

    const handleMessage = useCallback((chatMessage) => { 
        console.log(chatMessage);
        console.log(messageIdsRef.current);
        if (chatMessage.messageId && messageIdsRef.current.has(chatMessage.messageId)) { // Assuming the chat object has a 'message' property
            console.log("RoomPage: Received chat message:", chatMessage.content);
            messageIdsRef.current.delete(chatMessage.messageId); // Remove the message ID after processing
        } else {
            // If the message ID is new, add it to the set
            setLog(prevLog => [...prevLog, { sender: chatMessage.sender, 
                                            message: chatMessage.content, 
                                            messageId: chatMessage.messageId, 
                                            timestamp: chatMessage.timestamp,
                                            isLocal: false }]);
        }
    }, []);

    const handleSDPStatus = useCallback((status) => {
        console.log("RoomPage: Received SDP status update:", status);
        //SDP status updates can be logged or processed here
    }, []);

    const handleStompConnect = useCallback(() => {
        console.log("RoomPage: STOMP client re/connected. Attempting to (re)subscribe.");
        if (chatSubscriptionRef.current) {
            console.log("RoomPage: Unsubscribing from previous chat subscription.");
            chatSubscriptionRef.current.unsubscribe();
            chatSubscriptionRef.current = null;
        }
        if (sdpSubscriptionRef.current) {
            console.log("RoomPage: Unsubscribing from previous SDP subscription.");
            sdpSubscriptionRef.current.unsubscribe();
            sdpSubscriptionRef.current = null;
        }

        if (customSessionId) {
            chatSubscriptionRef.current = subscribeToChat(customSessionId, handleMessage);
        } else {
            console.warn("RoomPage: customSessionId not available, cannot subscribe to chat.");
        }

        // Subscribe to SDP status
        sdpSubscriptionRef.current = subscribeToSDPStatus(handleSDPStatus);
    }, [customSessionId, handleMessage, handleSDPStatus]);
    
    useEffect(() => {
        console.log("RoomPage: Main WebSocket useEffect running." );
        //It will activate the STOMP client if it's not already active. 
        connectWebSocket();

        //Register the re-subscription handler to be called on every connect 
        addOnConnectCallback(handleStompConnect);
        
        return () => {
            console.log("RoomPage: Cleaning up WebSocket connections and subscriptions for RoomPage.");

            // Remove the onConnect callback specific to this component instance
            removeOnConnectCallback(handleStompConnect);

            // Explicitly unsubscribe from chat and SDP topics
            if (chatSubscriptionRef.current) {
                console.log("RoomPage: Unsubscribing chat on cleanup.");
                chatSubscriptionRef.current.unsubscribe();
                chatSubscriptionRef.current = null;
            }
            if (sdpSubscriptionRef.current) {
                console.log("RoomPage: Unsubscribing SDP on cleanup.");
                sdpSubscriptionRef.current.unsubscribe();
                sdpSubscriptionRef.current = null;
            }

            //disconnectWebSocket();
            messageIdsRef.current.clear(); // Clear message IDs on cleanup
        };
    }, [handleStompConnect]);

   
    useEffect(() => {
        const janusInstance = getJanusInstance();
        const audioPluginHandle = getAudioPluginHandle();

      if (!janusInstance || !audioPluginHandle || !customSessionId) {
        console.error("Missing Janus instance or plugin handle. Redirecting to home.");
        navigate('/');
        return;
        }

      // WebSocket connection for chat and SDP status
      connectWebSocket(handleMessage); 

      // DO NOT re-assign onlocalstream, onremotetrack, onremotestream here.
        // These callbacks should be set ONLY ONCE during plugin attachment in ConnectingPage.js.

        // Instead, check if streams are ALREADY available on the plugin handle
        // (because ConnectingPage.js should have attached them)
        const localStream = getLocalMediaStream();
        if (localStream) {
            console.log("RoomPage: Local stream found on handle:", localStream);
            console.log("RoomPage: Local stream active?", localStream.active, "Tracks:", localStream.getAudioTracks());
            if (localAudioRef.current) {
                Janus.attachMediaStream(localAudioRef.current, localStream);
                localAudioRef.current.play().catch(e => {
                    console.error("RoomPage: Error playing local audio stream (autoplay prevented):", e);
                });
            }
            if (localCanvasRef.current) {
                drawAudioVisualizer(localStream, localCanvasRef, localAnalyserRef, localAnimationFrameId, "Local Audio");
            }
        } else {
            console.log("RoomPage: No local stream found on handle initially.");
            // If local stream is not immediately available, you might need a way for ConnectingPage
            // to notify RoomPage when it becomes available, or ensure local stream is always ready before navigating.
            // For now, we rely on it being present.
        }

        // For remote streams, it's more complex with multiple participants.
        // The `onremotetrack` in ConnectingPage should manage adding/removing tracks.
        // Here, we check for a potential single remote stream, or assume `onremotetrack` in ConnectingPage
        // is pushing streams to the relevant audio elements.
        const remoteStreams = getAllRemoteMediaStreams();
        if (remoteStreams.length > 0) {
            console.log("RoomPage: Remote streams found in WebRTCManager:", remoteStreams);
            // For simplicity, using the first remote stream found for visualizer
            // If you have multiple remote users, you'll need to adapt this to draw for each
            const firstRemoteStream = remoteStreams[0];
            if (remoteAudioRef.current) { // Assuming remoteAudioRef is for a single remote stream
                Janus.attachMediaStream(remoteAudioRef.current, firstRemoteStream);
                remoteAudioRef.current.play().catch(e => {
                    console.error("RoomPage: Error playing remote audio stream (autoplay prevented):", e);
                });
            }
            if (remoteCanvasRef.current) {
                drawAudioVisualizer(firstRemoteStream, remoteCanvasRef, remoteAnalyserRef, remoteAnimationFrameId, "Remote Audio");
            }
        } else {
            console.log("RoomPage: No remote streams found in WebRTCManager initially.");
        }
       

        // If RoomPage needs to send SDP offers (e.g., for media configuration changes)
        // audioPluginHandle.createOffer({
        //     media: { audioRecv: false, audioSend: true, videoRecv: false, videoSend: false },
        //     success: (jsep) => {
        //         console.log("Got SDP offer from RoomPage:", jsep);
        //         sendCommand('/sdp/offer', { customSessionId: customSessionId, jsep: jsep });
        //     },
        //     error: (error) => {
        //         console.error("WebRTC error creating offer in RoomPage:", error);
        //     }
        // });

      // Initialize Janus in RoomPage
     
      // Cleanup function
      return () => {
        // Ensure you unsubscribe only if the subscription object exists
        if (chatSubscriptionRef.current) {
            chatSubscriptionRef.current.unsubscribe();
            chatSubscriptionRef.current = null; // Clear the ref
        }
        if (sdpSubscriptionRef.current) {
            sdpSubscriptionRef.current.unsubscribe();
            sdpSubscriptionRef.current = null; // Clear the ref
        }
        disconnectWebSocket(); // Disconnect the WebSocket
        // ... existing Janus cleanup
    };
  }, [navigate, customSessionId, drawAudioVisualizer]);


  useEffect(() => {
    let sessionStatusSubscription = null;

    const handleSessionStatus = (response) => {
        console.log("Received session status:", response);
        // Assuming your backend sends a clear message on recording status changes
        if (response.data && response.data.includes("Recording started")) {
            setIsRecording(true);
        } else if (response.data && response.data.includes("Recording stopped")) {
            setIsRecording(false);
        }
    };

    const subscribeAndConnect = async () => {
        await connectWebSocket();
        sessionStatusSubscription = subscribeToSessionStatus(handleSessionStatus);
    };

    subscribeAndConnect();

    return () => {
        if (sessionStatusSubscription) {
            sessionStatusSubscription.unsubscribe();
        }
    };
}, []);


   // Mute/Unmute functionality (example)
   const toggleMute = () => {
        const audioPluginHandle = getAudioPluginHandle();
      if (audioPluginHandle) {
          const muted = !audioPluginHandle.isLocalAudioMuted();
          audioPluginHandle.send({ message: { request: "configure", muted: muted } });
          setLog((prevLog) => [...prevLog, `Local audio ${muted ? 'muted' : 'unmuted'}`]);
        }
    };

    const handleLeaveSession = () => {
        sendCommand('/session/leave', {
            customSessionId: customSessionId,
            userId: 'user123',
        });
        navigate('/home');
    };

    const startRecording = () => {
        const audioPluginHandle = getAudioPluginHandle();
        const janusInstance = getJanusInstance();
        if (!audioPluginHandle || !janusInstance) {
            console.error("Cannot start recording, missing Janus instance or plugin handle.");
            return;
        } 
        sendCommand('/session/record/start', {
            customSessionId: Number(customSessionId),
            streamSessionId: Number(janusInstance.getSessionId()),
            streamHandleId: Number(audioPluginHandle.id)
        });
    };

    const pauseRecording = () => {
        const audioPluginHandle = getAudioPluginHandle();
        const janusInstance = getJanusInstance();
        console.log("Pausing recording - JanusInstance:", janusInstance, "AudioPluginHandle:", audioPluginHandle);
        if (!audioPluginHandle || !janusInstance) {
            console.error("Cannot start recording, missing Janus instance or plugin handle.");
            return;
        } 
        sendCommand('/session/record/pause', {
            customSessionId: Number(customSessionId),
            streamSessionId: Number(janusInstance.getSessionId()),
            streamHandleId: Number(audioPluginHandle.id)
        });
    };

   const resumeRecording = () => {
    const audioPluginHandle = getAudioPluginHandle();
        const janusInstance = getJanusInstance();
        if (!audioPluginHandle || !janusInstance) {
            console.error("Cannot start recording, missing Janus instance or plugin handle.");
            return;
        } 
        sendCommand('/session/record/resume', {
            customSessionId: Number(customSessionId),
            streamSessionId: Number(janusInstance.getSessionId()),
            streamHandleId: audioPluginHandle.id
        });
    };

    const stopRecording = () => {
        const audioPluginHandle = getAudioPluginHandle();
        const janusInstance = getJanusInstance();
        if (!audioPluginHandle || !janusInstance) {
            console.error("Cannot start recording, missing Janus instance or plugin handle.");
            return;
        } 
        sendCommand('/session/record/stop', {
            customSessionId: Number(customSessionId),
            streamSessionId: Number(janusInstance.getSessionId()),
            streamHandleId: audioPluginHandle.id
        });
    };

    const toggleDirectorVoice = () => {
        const newState = !directorVoiceEnabled; 
        sendCommand('/session/director-voice', {
            customSessionId: Number(customSessionId),
            enable: true, 
        });
        setDirectorVoiceEnabled(newState);
    };
  
      const sendMessage = useCallback((sender, content) => {
        const messageId = uuidv4();
        const timestamp = new Date().toISOString();

        const messagePayload = {
            messageId: messageId,
            sender: sender,
            content: content,
            timestamp: timestamp
        };
        messageIdsRef.current.add(messageId);
        sendCommand(`/room/chat/${customSessionId}`, messagePayload);
        setChatInput('');
        setLog(prevLog => [...prevLog, { sender: sender, 
                                        message: content, 
                                        messageId: messageId, 
                                        timestamp: timestamp,
                                        isLocal: true }]);
    }, [customSessionId, setChatInput]);


    return (
        <div className="min-h-screen text-white p-4 sm:p-8 font-sans flex">
            {/* Left Column: Main Controls and Audio Visualizers */}
            <div className="w-1/2 flex flex-col items-center-p-4">
                <div className="text-center mb-8">
                    <h2>Room ID: {customSessionId}</h2>
                    <button onClick={handleLeaveSession} className="bg-red-700 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200">
                        Leave Session
                    </button>
                    <div className="flex justify-center space-x-4 mt-4">
                        <button onClick={startRecording} /*disabled={isRecording}*/ className="bg-green-800 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200">
                            <i className="fas fa-circle"></i> 
                        </button>
                        <button onClick={pauseRecording} /*disabled={!isRecording}*/ className="bg-yellow-800 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200">
                            <i className="fas fa-pause"></i> 
                        </button>
                        <button onClick={resumeRecording} disabled={!isRecording} className="bg-blue-800 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200">
                            <i className="fas fa-play"></i> 
                        </button>
                        <button onClick={stopRecording} disabled={!isRecording} className="bg-gray-800 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200">
                            <i className="fas fa-stop"></i> 
                        </button>
                    </div>
                </div>
                <button onClick={toggleDirectorVoice} className="bg-purple-800 hover:bg-purple-600 text-white font-bold py-1 px-3 text-sm rounded-md transition-colors duration-200 mt-4 w-fit mx-auto">
                    {directorVoiceEnabled ? 'Disable' : 'Enable'} Director Voice
                </button>

                {/*Audio Visualizers Section */}
                <div className="mt-8 flex flex-col items-center space-y-4">
                <p className="text-sm text-gray-400">Local Audio</p>
                <audio id="localAudio" autoPlay controls className="hidden" ref={localAudioRef} />
                <canvas id="localCanvas" width="300" height="60" className="bg-black rounded-md shadow-md" ref={localCanvasRef} />

                <p className="text-sm text-gray-400 mt-6">Remote Audio</p>
                <audio id="remoteAudio" autoPlay controls className="hidden" ref={remoteAudioRef} />
                <canvas id="remoteCanvas" width="300" height="60" className="bg-black rounded-md shadow-md" ref={remoteCanvasRef} />
                </div>
            </div>

            {/* Chat Section */}
            <div className="w-1/3 border-l border-gray-700 p-4 flex flex-col ml-auto">
                <h3 className="text-lg font-bold mb-4">Chat</h3>
                <div className="mt-4 overflow-y-auto border rounded p-2 bg-gray-800 bg-opacity-75 text-gray-100 flex-1">
                    {log.map((msg, index) => (
                         <div key={index} className={`mb-2 ${msg.isLocal ? 'text-left' : 'text-right'}`}>
                            <span className="font-bold">{msg.sender}:</span> {msg.message}
                            {msg.timestamp && ( // Conditionally render if timestamp exists
                                <div className={`text-xs mt-1 ${msg.isLocal ? 'text-blue-200' : 'text-gray-400'}`}>
                                    {new Date(msg.timestamp).toLocaleTimeString()} {/* Format the timestamp */}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex flex-col space-y-2 mt-4">
                    <input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Type your message here..."
                        className="border rounded py-2 px-4 w-full bg-gray-800 bg-opacity-75 text-gray-100"
                    />
                    <button onClick={() => sendMessage('User', chatInput)} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                        Send message
                    </button>
                </div>
            </div>            
        </div>
    );
};

export default RoomPage;